import React, { useMemo } from 'react';
import { useFrame } from '@react-three/fiber';
import * as THREE from 'three';
import { brainVertex, brainFragment } from './shaders';
import { useSimStore } from '../state/store';

const GOLD = new THREE.Color('#ffd700');
const CRIMSON = new THREE.Color('#ff2a2a');

export default function BrainMesh({
  clipPlanes = null,
  tint = '#93a6b8',
  opacity = 0.09,
  matte = false,
  glow = null,
  glowStrength = 0,
  compareEvent = null,
}) {
  const geometry = useMemo(() => {
    const geo = new THREE.IcosahedronGeometry(0.6, 4);
    const pos = geo.attributes.position;
    const v = new THREE.Vector3();
    for (let i = 0; i < pos.count; i++) {
      v.fromBufferAttribute(pos, i);
      v.x *= 1.0;
      v.y *= 0.82;
      v.z *= 1.12;
      const len = v.length();
      const dir = v.clone().normalize();
      const n =
        0.028 * Math.sin(v.x * 12.0 + v.y * 9.0) * Math.cos(v.z * 10.0) +
        0.016 * Math.sin(v.z * 16.0 + v.y * 7.0);
      let r = len + n;
      const fissure = Math.exp(-(v.x * v.x) / 0.0022) * 0.1; // deep longitudinal cleft
      r -= fissure;
      v.copy(dir).multiplyScalar(r);
      pos.setXYZ(i, v.x, v.y, v.z);
    }
    geo.computeVertexNormals();
    return geo;
  }, []);

  // Stylized brainstem + spinal cord dropping from the center-base of the brain,
  // so each exhibit reads as a single anatomical unit.
  const stemGeometry = useMemo(() => {
    const curve = new THREE.CatmullRomCurve3([
      new THREE.Vector3(0, -0.16, 0.06),
      new THREE.Vector3(0, -0.32, 0.01),
      new THREE.Vector3(0, -0.54, -0.05),
      new THREE.Vector3(0, -0.8, -0.07),
      new THREE.Vector3(0, -1.06, -0.05),
    ]);
    return new THREE.TubeGeometry(curve, 56, 0.058, 16, false);
  }, []);

  // Pons/medulla bulge where the stem meets the base.
  const ponsGeometry = useMemo(() => {
    const g = new THREE.SphereGeometry(0.1, 20, 16);
    g.scale(1.05, 0.95, 1.35);
    g.translate(0, -0.17, 0.03);
    return g;
  }, []);

  const uniforms = useMemo(
    () => ({
      uColor: { value: new THREE.Color(tint) },
      uOpacity: { value: opacity },
      uMatte: { value: matte ? 1 : 0 },
      uFlash: { value: 0 },
      uFlashColor: { value: GOLD.clone() },
      uGlow: { value: new THREE.Color(glow || '#000000') },
      uGlowStrength: { value: glowStrength },
      uFlashSide: { value: 0 },
    }),
    [tint, opacity, matte, glow, glowStrength]
  );

  // A single shared material so the brain shell AND the brainstem always render
  // with an identical look (Blue glass / Matte White / Smoke Gray + cyan glow).
  const material = useMemo(
    () =>
      new THREE.ShaderMaterial({
        uniforms,
        vertexShader: brainVertex,
        fragmentShader: brainFragment,
        transparent: true,
        depthWrite: false,
        side: THREE.DoubleSide,
        clipping: !!clipPlanes,
        clippingPlanes: clipPlanes || [],
      }),
    [uniforms, clipPlanes]
  );

  useFrame(() => {
    const s = useSimStore.getState();
    const u = material.uniforms;
    const now = performance.now() / 1000;
    let flash = 0;
    const ev = compareEvent || (s.event !== 'none' ? s.event : null);
    if (ev === 'win') {
      const et = compareEvent ? now % 2.6 : now - s.eventStart;
      u.uFlashColor.value.copy(GOLD);
      flash = Math.max(0, Math.sin(Math.min(1, et / 2.6) * Math.PI));
    } else if (ev === 'loss') {
      const et = compareEvent ? now % 1.4 : now - s.eventStart;
      u.uFlashColor.value.copy(CRIMSON);
      flash = Math.max(0, 1 - et / 0.9);
    } else if (ev === 'clutch') {
      const et = now - s.eventStart;
      u.uFlashColor.value.copy(CRIMSON);
      flash = (0.5 + 0.5 * Math.sin(et * 22.0)) * 0.5;
    }
    u.uFlash.value = flash;
    u.uFlashSide.value = ev === 'win' ? 1 : ev === 'loss' ? -1 : 0;
  });

  const wireOpacity = matte ? 0.05 : 0.09;

  return (
    <group>
      <mesh geometry={geometry} material={material} />
      <mesh geometry={ponsGeometry} material={material} />
      <mesh geometry={stemGeometry} material={material} />

      {/* wireframe overlays share the tint so the stem matches the shell */}
      <mesh geometry={geometry}>
        <meshBasicMaterial color={tint} wireframe transparent opacity={wireOpacity} depthWrite={false} clippingPlanes={clipPlanes || []} />
      </mesh>
      <mesh geometry={stemGeometry}>
        <meshBasicMaterial color={tint} wireframe transparent opacity={wireOpacity} depthWrite={false} clippingPlanes={clipPlanes || []} />
      </mesh>
    </group>
  );
}
